/**
 * Bootstrap API：登入後一次取得 userTenants、tenant、featureFlags、member，
 * 並可將結果寫入 SWR 快取以減少後續重複請求。
 */

import { mutate } from 'swr';

const API_BASE =
  typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_BASE_URL
    ? import.meta.env.VITE_API_BASE_URL
    : 'http://localhost:8787';

export interface BootstrapUserTenantItem {
  tenant_id: string;
  tenant_name: string;
  tenant_slug: string;
  role: string;
}

export interface BootstrapUserTenants {
  user_id: string;
  tenants: BootstrapUserTenantItem[];
}

export interface BootstrapData {
  userTenants: BootstrapUserTenants;
  tenant: Record<string, unknown> | null;
  featureFlags: string[] | null;
  member: Record<string, unknown> | null;
}

export interface BootstrapResponse {
  success: boolean;
  data: BootstrapData;
  timestamp: string;
}

/**
 * 呼叫 GET /api/bootstrap，帶 JWT 與選填 X-Tenant-ID
 */
export async function fetchBootstrap(
  token: string,
  tenantId?: string | null
): Promise<BootstrapResponse> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
  };
  if (tenantId) {
    headers['X-Tenant-ID'] = tenantId;
  }
  const res = await fetch(`${API_BASE}/api/bootstrap`, {
    method: 'GET',
    headers,
  });
  if (!res.ok) {
    const errBody = await res.json().catch(() => ({}));
    const err = new Error(
      (errBody as { message?: string })?.message || `HTTP ${res.status}`
    ) as Error & { status?: number };
    err.status = res.status;
    throw err;
  }
  return res.json() as Promise<BootstrapResponse>;
}

/**
 * 將 Bootstrap 回傳寫入 SWR 快取，讓 useTenant、useFeatureFlags、useCurrentUserRole 等命中快取
 */
export function hydrateSwrFromBootstrap(
  data: BootstrapData,
  tenantId: string | null,
  token: string | null
): void {
  if (!token) return;

  // useTenantsGetMe 的 key 為 ['/api/tenants/me']，快取值為 { data, status, headers }
  if (data.tenant && tenantId) {
    mutate(
      ['/api/tenants/me'] as const,
      {
        data: {
          success: true,
          data: data.tenant,
          timestamp: new Date().toISOString(),
        },
        status: 200,
        headers: new Headers(),
      },
      { revalidate: false }
    );
  }

  // useFeatureFlags 的 key 為 `/api/tenants/${tenantId}/feature-flags`
  if (data.featureFlags && tenantId) {
    mutate(
      `/api/tenants/${tenantId}/feature-flags`,
      { success: true, data: data.featureFlags },
      { revalidate: false }
    );
  }

  // useCurrentUserRole（members/me）的 key 為 ['/api/members/me', tenantId, token]
  if (data.member && tenantId && token) {
    mutate(
      ['/api/members/me', tenantId, token] as const,
      { success: true, data: data.member },
      { revalidate: false }
    );
  }
}
